/**
 * Input Sanitization Service
 * 
 * Client-side wrapper for server-side input sanitization Cloud Function.
 * Prevents XSS, injection attacks, and malicious content.
 * 
 * Usage: Call sanitizeInput() before saving any user-generated content.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface SanitizeOptions {
  maxLength?: number;        // Max string length (default: 10000)
  allowHTML?: boolean;       // Allow HTML tags (default: false)
  allowURLs?: boolean;       // Allow URLs (default: true)
  stripWhitespace?: boolean; // Strip extra whitespace (default: true)
  strictMode?: boolean;      // Extra strict sanitization (default: false)
}

interface SanitizeResult {
  sanitized: any;
  original?: any;
  modified: boolean;
  warnings?: string[];
}

/**
 * Sanitize user input through Cloud Function
 * 
 * SECURITY: This function FAILS CLOSED, not open.
 * If sanitization fails, the request is rejected (not bypassed).
 * 
 * @param input - The data to sanitize (string, object, or array)
 * @param options - Sanitization options
 * @returns Sanitized data with metadata
 * @throws Error if sanitization service is unavailable
 */
export async function sanitizeInput(
  input: any,
  options: SanitizeOptions = {}
): Promise<SanitizeResult> {
  try {
    const sanitizeFunction = httpsCallable(functions, 'sanitizeUserInput');
    
    const result = await sanitizeFunction({
      input,
      options
    });

    const response = result.data as any;
    
    if (!response.success) {
      throw new Error(response.message || 'Sanitization failed');
    }

    return response.data as SanitizeResult;
  } catch (error: any) {
    console.error('❌ Input sanitization error:', error);
    
    // 🔒 SECURITY: FAIL CLOSED, NOT OPEN!
    // Never return unsanitized data. Reject the request instead.
    // This prevents complete security bypass during outages.
    throw new Error(
      'Unable to process your request at this time. Please try again in a few moments. ' +
      'If the problem persists, please contact support.'
    );
  }
}

/**
 * Sanitize quest data before saving
 * @param title - Quest title
 * @param description - Quest description
 * @returns Sanitized title and description
 */
export async function sanitizeQuestInput(
  title: string,
  description: string
): Promise<{ title: string; description: string; warnings: string[] }> {
  const warnings: string[] = [];
  
  // Sanitize title (no HTML, no URLs, max 200 chars, strict mode for SQL/XSS)
  const titleResult = await sanitizeInput(title, {
    maxLength: 200,
    allowHTML: false,
    allowURLs: false,
    stripWhitespace: true,
    strictMode: true  // ✅ Enable strict mode to remove SQL injection patterns
  });
  
  if (titleResult.modified) {
    warnings.push(...(titleResult.warnings || []));
  }
  
  // Sanitize description (no HTML, allow URLs, max 2000 chars, strict mode for SQL/XSS)
  const descResult = await sanitizeInput(description, {
    maxLength: 2000,
    allowHTML: false,
    allowURLs: true,
    stripWhitespace: true,
    strictMode: true  // ✅ Enable strict mode to remove SQL injection patterns
  });
  
  if (descResult.modified) {
    warnings.push(...(descResult.warnings || []));
  }
  
  return {
    title: titleResult.sanitized,
    description: descResult.sanitized,
    warnings
  };
}

/**
 * Sanitize program data before saving
 * @param goal - Program goal
 * @param title - Program title (optional)
 * @returns Sanitized data
 */
export async function sanitizeProgramInput(
  goal: string,
  title?: string
): Promise<{ goal: string; title?: string; warnings: string[] }> {
  const warnings: string[] = [];
  
  // Sanitize goal
  const goalResult = await sanitizeInput(goal, {
    maxLength: 500,
    allowHTML: false,
    allowURLs: false,
    stripWhitespace: true,
    strictMode: true  // ✅ Enable strict mode to remove SQL injection patterns
  });
  
  if (goalResult.modified) {
    warnings.push(...(goalResult.warnings || []));
  }
  
  // Sanitize title if provided
  let sanitizedTitle: string | undefined;
  if (title) {
    const titleResult = await sanitizeInput(title, {
      maxLength: 200,
      allowHTML: false,
      allowURLs: false,
      stripWhitespace: true,
      strictMode: true  // ✅ Enable strict mode to remove SQL injection patterns
    });
    
    if (titleResult.modified) {
      warnings.push(...(titleResult.warnings || []));
    }
    
    sanitizedTitle = titleResult.sanitized;
  }
  
  return {
    goal: goalResult.sanitized,
    title: sanitizedTitle,
    warnings
  };
}

/**
 * Sanitize user profile updates
 * @param displayName - User display name
 * @returns Sanitized display name
 */
export async function sanitizeProfileInput(
  displayName: string
): Promise<{ displayName: string; warnings: string[] }> {
  const warnings: string[] = [];
  
  const result = await sanitizeInput(displayName, {
    maxLength: 50,
    allowHTML: false,
    allowURLs: false,
    strictMode: true // Extra strict for display names
  });
  
  if (result.modified) {
    warnings.push(...(result.warnings || []));
  }
  
  return {
    displayName: result.sanitized,
    warnings
  };
}

/**
 * Sanitize passive skill data
 * @param name - Skill name
 * @param description - Skill description
 * @returns Sanitized data
 */
export async function sanitizePassiveSkillInput(
  name: string,
  description: string
): Promise<{ name: string; description: string; warnings: string[] }> {
  const warnings: string[] = [];
  
  // Sanitize name
  const nameResult = await sanitizeInput(name, {
    maxLength: 100,
    allowHTML: false,
    allowURLs: false,
    stripWhitespace: true,
    strictMode: true  // ✅ Enable strict mode to remove SQL injection patterns
  });
  
  if (nameResult.modified) {
    warnings.push(...(nameResult.warnings || []));
  }
  
  // Sanitize description
  const descResult = await sanitizeInput(description, {
    maxLength: 500,
    allowHTML: false,
    allowURLs: false,
    stripWhitespace: true,
    strictMode: true  // ✅ Enable strict mode to remove SQL injection patterns
  });
  
  if (descResult.modified) {
    warnings.push(...(descResult.warnings || []));
  }
  
  return {
    name: nameResult.sanitized,
    description: descResult.sanitized,
    warnings
  };
}

export const inputSanitizationService = {
  sanitizeInput,
  sanitizeQuestInput,
  sanitizeProgramInput,
  sanitizeProfileInput,
  sanitizePassiveSkillInput
};

